import { z } from 'zod'
import { clientSchema, portfolioHealthSchema } from './clients.js'
import { followupsByOwnerSchema } from './collab.js'
import { metricValueSchema } from './metrics.js'

export const alertSchema = z.object({
  id: z.string(),
  severity: z.enum(['critical', 'warning']),
  kind: z.enum([
    'metric_missing',
    'metric_critical',
    'data_expired',
    'client_risk',
  ]),
  titulo: z.string(),
  detalle: z.string(),
  /** Ruta interna a la pantalla donde se resuelve. */
  href: z.string(),
})
export type Alert = z.infer<typeof alertSchema>

export const mrrTrendPointSchema = z.object({
  periodo: z.string(),
  mrr: z.number(),
  nuevoMrr: z.number(),
  churnedMrr: z.number(),
})
export type MrrTrendPoint = z.infer<typeof mrrTrendPointSchema>

export const funnelSummarySchema = z.object({
  etapas: z.array(
    z.object({
      nombre: z.string(),
      cantidad: z.number(),
      /** Conversión desde la etapa anterior. null en la primera. */
      conversionPct: z.number().nullable(),
    }),
  ),
  periodo: z.string(),
})
export type FunnelSummary = z.infer<typeof funnelSummarySchema>

/** Overview del founder: la empresa completa, con $$. */
export const adminOverviewSchema = z.object({
  rol: z.literal('admin'),
  periodo: z.string(),
  heroMetrics: z.array(metricValueSchema),
  mrrTrend: z.array(mrrTrendPointSchema),
  portfolio: portfolioHealthSchema,
  funnel: funnelSummarySchema,
  alerts: z.array(alertSchema),
  followupsByOwner: followupsByOwnerSchema,
})

/** Overview de ops: la cola de trabajo, sin $$. */
export const opsOverviewSchema = z.object({
  rol: z.literal('ops'),
  periodo: z.string(),
  heroMetrics: z.array(metricValueSchema),
  /** Pendientes de implementación ordenados por antigüedad. */
  colaImplementacion: z.array(clientSchema),
  /** Clientes con mayor brecha de activación. */
  brechaCritica: z.array(clientSchema),
  /** Clientes en estado retención. */
  enRiesgo: z.array(clientSchema),
  alerts: z.array(alertSchema),
  misFollowups: followupsByOwnerSchema,
})

/** Overview comercial: el funnel, sin $$ global. */
export const comercialOverviewSchema = z.object({
  rol: z.literal('comercial'),
  periodo: z.string(),
  heroMetrics: z.array(metricValueSchema),
  funnel: funnelSummarySchema,
  canales: z.array(
    z.object({
      nombre: z.string(),
      status: z.string(),
      escalabilidad: z.enum(['alta', 'media', 'baja']),
      prioridad: z.string(),
    }),
  ),
  /** Cuentas en estado expansión: la palanca subutilizada. */
  oportunidadesExpansion: z.array(clientSchema),
  alerts: z.array(alertSchema),
  misFollowups: followupsByOwnerSchema,
})

export const overviewPayloadSchema = z.discriminatedUnion('rol', [
  adminOverviewSchema,
  opsOverviewSchema,
  comercialOverviewSchema,
])
export type OverviewPayload = z.infer<typeof overviewPayloadSchema>
export type AdminOverview = z.infer<typeof adminOverviewSchema>
export type OpsOverview = z.infer<typeof opsOverviewSchema>
export type ComercialOverview = z.infer<typeof comercialOverviewSchema>
